/**
 * الإعدادات متعددة المستويات وقواعد الأولوية.
 *
 * الأولوية: المستخدم > الفرع > البراند > الشركة > المنصة.
 * كل مستوى يُدمج فوق ما فوقه (دمج سطحي للكائنات)، فلا يمكن لمستوى أدنى أن
 * يحذف قيمة، بل يستبدلها فقط. هذا يمنع اختفاء القيم الافتراضية بصمت.
 */

import type { RecurrenceRule, SettingRecord, SettingsScope, ThemeTokens } from './types';
import { DEFAULT_RECURRENCE } from './recurrence';

export const SETTING_KEYS = {
  timeZone: 'company.time_zone',
  locale: 'company.locale',
  defaultRecurrence: 'tasks.default_recurrence',
  autoGenerateRecurring: 'tasks.auto_generate_recurring',
  completedTaskRetention: 'tasks.completed_retention',
  approvalPolicy: 'tasks.approval_policy',
  personalTaskPolicy: 'tasks.personal_policy',
  notificationPolicy: 'notifications.policy',
  retentionPolicy: 'attachments.retention',
  theme: 'ui.theme',
  privacyPolicy: 'company.privacy',
  uploadPolicy: 'attachments.upload_policy',
  operationalFeatures: 'company.operational_features',
  mediaHubPolicy: 'company.media_hub_policy',
  templateChecklists: 'tasks.template_checklists',
} as const;

export type SettingKey = (typeof SETTING_KEYS)[keyof typeof SETTING_KEYS];

export interface ApprovalPolicy {
  /** هل تتطلب المهام الجديدة اعتمادًا افتراضيًا */
  defaultRequiresApproval: boolean;
  /** هل يسمح باعتماد المنفّذ لإنجازه بنفسه */
  allowSelfApproval: boolean;
  /** اعتماد تلقائي عند انقضاء المدة (غير مُنفَّذ؛ يظهر في الواجهة كغير مفعّل) */
  autoApproveAfterHours: number | null;
}

export interface PersonalTaskPolicy {
  /** هل يمكن للمستخدم إنشاء مهام شخصية */
  enabledForAllMembers: boolean;
  /** هل يسمح بمنح صلاحية الاطلاع الإداري على المهام الشخصية */
  allowAdminVisibility: boolean;
}

export interface NotificationPolicy {
  /** تذكير قبل الموعد النهائي بهذا العدد من الدقائق */
  dueSoonLeadMinutes: number;
  /** إشعار المتأخرة */
  overdueEnabled: boolean;
  /** تصعيد المتأخرة إلى المشرف */
  escalateToSupervisor: boolean;
  /** بعد كم ساعة تأخير يتم التصعيد */
  escalateAfterHours: number;
  /** أقصى عدد مستويات تصعيد في السلسلة */
  maxEscalationLevels: number;
}

export interface RetentionPolicy {
  /** indefinite = الاحتفاظ إلى أجل غير مسمى */
  mode: 'indefinite' | 'by_age';
  days: number | null;
  /** هل تقتصر السياسة على الصور */
  imagesOnly: boolean;
  /** الحد الأدنى للنطاق: هل تُطبق على مرفقات المهام المكتملة فقط */
  completedTasksOnly: boolean;
}

export interface CompletedTaskRetentionPolicy {
  /** تفعيل الحذف التلقائي للمهام المكتملة بعد مرور المدة المحددة */
  autoDeleteEnabled: boolean;
  /** المدة بالأيام بعد إكمال المهمة لحذفها تلقائيًا */
  autoDeleteAfterDays: number;
  /** الاحتفاظ بعدد المهام المكتملة في البطاقة حتى بعد حذف المهام (يدويًا أو تلقائيًا) */
  preserveCompletedCount: boolean;
  /** العدّاد التراكمي للمهام المكتملة التي تم حذفها سابقًا */
  archivedCompletedCount: number;
}

export interface OperationalFeaturesConfig {
  /** شات الموظفين والمشرفين الداخلي */
  staffChatEnabled: boolean;
  /** تبويبة التعليمات والبيانات والقرارات الإدارية */
  bulletinsEnabled: boolean;
  /** قسم الملفات والوسائط الشامل */
  mediaHubEnabled: boolean;
  /** إلزام التصوير المباشر من الكاميرا فقط عند رفع صور الإثبات */
  cameraOnlyProof: boolean;
  /** الختم المائي التلقائي (التاريخ + الوقت + الفرع + اسم الموظف) على الصور */
  photoWatermarkEnabled: boolean;
  /** الإنجاز السريع للمهام والجولات من اللوحة مباشرة */
  quickCompleteEnabled: boolean;
  /** قوائم الفحص (Checklist) داخل قوالب التكرار */
  templateChecklistsEnabled: boolean;
  /** الإغلاق التلقائي للجولات الساعية الفائتة عند دخول الجولة التالية */
  autoExpireHourlyRounds: boolean;
  /** رموز QR لنقاط الفحص في الفروع */
  qrCheckpointsEnabled: boolean;
  /** التعليقات والملاحظات الصوتية داخل المهمة */
  taskCommentsEnabled: boolean;
  /** تقرير تسليم الوردية */
  shiftHandoverEnabled: boolean;
  /** لوحة ترتيب الفروع وبطاقة تقييم الموظفين في التقارير */
  branchLeaderboardEnabled: boolean;
  /** الدليل التشغيلي للمتجر وقسم دور كل موظف */
  manualEnabled: boolean;
}

export interface MediaHubRetentionPolicy {
  /** تفعيل الحذف التلقائي للوسائط والملفات */
  autoDeleteEnabled: boolean;
  /** حذف الملفات الأقدم من هذا العدد من الأيام */
  autoDeleteAfterDays: number;
  /** هل يشمل الحذف مرفقات وإثباتات المهام */
  deleteTaskProofMedia: boolean;
  /** هل يقتصر حذف مرفقات المهام على المهام المكتملة فقط */
  completedTasksOnly: boolean;
  /** هل يشمل الحذف وسائط الشات والمحادثات والتعليقات */
  deleteChatMedia: boolean;
  /** هل يشمل الحذف مرفقات التعليمات والبيانات الإدارية */
  deleteBulletinMedia: boolean;
  /** أنواع الملفات المشمولة بالحذف */
  deleteImages: boolean;
  deleteVideos: boolean;
  deleteAudio: boolean;
  deleteDocuments: boolean;
}

export interface UploadPolicy {
  maxSizeMb: number;
  allowedMimeTypes: string[];
}

export interface PrivacyPolicy {
  /** أقصى مدة للاطلاع الإداري على المهام الشخصية قبل إعادة الطلب */
  personalAccessRequiresReason: boolean;
}

export interface TemplateChecklistStepDef {
  title: string;
  isRequired: boolean;
}

export const DEFAULT_APPROVAL_POLICY: ApprovalPolicy = {
  defaultRequiresApproval: false,
  allowSelfApproval: false,
  autoApproveAfterHours: null,
};

export const DEFAULT_PERSONAL_TASK_POLICY: PersonalTaskPolicy = {
  enabledForAllMembers: true,
  allowAdminVisibility: false,
};

export const DEFAULT_NOTIFICATION_POLICY: NotificationPolicy = {
  dueSoonLeadMinutes: 60,
  overdueEnabled: true,
  escalateToSupervisor: true,
  escalateAfterHours: 4,
  maxEscalationLevels: 3,
};

export const DEFAULT_RETENTION_POLICY: RetentionPolicy = {
  mode: 'indefinite',
  days: null,
  imagesOnly: true,
  completedTasksOnly: false,
};

export const DEFAULT_COMPLETED_TASK_RETENTION: CompletedTaskRetentionPolicy = {
  autoDeleteEnabled: false,
  autoDeleteAfterDays: 7,
  preserveCompletedCount: true,
  archivedCompletedCount: 0,
};

export const DEFAULT_OPERATIONAL_FEATURES: OperationalFeaturesConfig = {
  staffChatEnabled: true,
  bulletinsEnabled: true,
  mediaHubEnabled: true,
  cameraOnlyProof: false,
  photoWatermarkEnabled: true,
  quickCompleteEnabled: true,
  templateChecklistsEnabled: true,
  autoExpireHourlyRounds: true,
  qrCheckpointsEnabled: true,
  taskCommentsEnabled: true,
  shiftHandoverEnabled: true,
  branchLeaderboardEnabled: true,
  manualEnabled: true,
};

export const DEFAULT_MEDIA_HUB_POLICY: MediaHubRetentionPolicy = {
  autoDeleteEnabled: false,
  autoDeleteAfterDays: 14,
  deleteTaskProofMedia: true,
  completedTasksOnly: true,
  deleteChatMedia: true,
  deleteBulletinMedia: false,
  deleteImages: true,
  deleteVideos: true,
  deleteAudio: true,
  deleteDocuments: false,
};

export const DEFAULT_UPLOAD_POLICY: UploadPolicy = {
  maxSizeMb: 50,
  allowedMimeTypes: [
    // صور
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/heic',
    'image/heif',
    'image/avif',
    'image/bmp',
    // فيديو
    'video/mp4',
    'video/webm',
    'video/quicktime',
    'video/x-matroska',
    'video/x-msvideo',
    'video/3gpp',
    // صوت
    'audio/mpeg',
    'audio/mp4',
    'audio/wav',
    'audio/x-wav',
    'audio/ogg',
    'audio/webm',
    'audio/aac',
    // مستندات
    'application/pdf',
    'text/plain',
    'text/csv',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
};

/** امتدادات الملفات المقبولة في حقل الاختيار (للاقتراح فقط، والتحقق في الخدمة). */
export const UPLOAD_ACCEPT_ATTRIBUTE =
  'image/*,video/*,audio/*,application/pdf,text/plain,text/csv,.docx,.xlsx,.pptx';

/** أقصى حجم لصورة الملف الشخصي بعد الضغط (بالبايت). */
export const AVATAR_MAX_BYTES = 400_000;

export const DEFAULT_PRIVACY_POLICY: PrivacyPolicy = {
  personalAccessRequiresReason: true,
};

export const DEFAULT_THEME: ThemeTokens = {
  primary: '#1d6fd6',
  accent: '#65a30d',
  radius: 'lg',
  mode: 'system',
  fontFamily: "'Segoe UI', Tahoma, system-ui, -apple-system, sans-serif",
  logoAttachmentId: null,
};

const SCOPE_PRIORITY: Record<SettingsScope, number> = {
  platform: 0,
  company: 1,
  brand: 2,
  branch: 3,
  user: 4,
};

export interface SettingScopeChainEntry {
  scopeType: SettingsScope;
  scopeId: string;
}

/** بناء سلسلة النطاقات الفعّالة لمستند معيّن (من الأدنى أولوية إلى الأعلى). */
export function buildScopeChain(input: {
  companyId: string;
  brandId?: string | null;
  branchId?: string | null;
  userId?: string | null;
}): SettingScopeChainEntry[] {
  const chain: SettingScopeChainEntry[] = [{ scopeType: 'platform', scopeId: 'global' }];
  chain.push({ scopeType: 'company', scopeId: input.companyId });
  if (input.brandId) chain.push({ scopeType: 'brand', scopeId: input.brandId });
  if (input.branchId) chain.push({ scopeType: 'branch', scopeId: input.branchId });
  if (input.userId) chain.push({ scopeType: 'user', scopeId: input.userId });
  return chain;
}

export function buildScopedSettingKey(
  baseKey: string,
  scopeType: SettingsScope,
  scopeId: string,
): string {
  if (scopeType === 'brand') return `${baseKey}__brand__${scopeId}`;
  if (scopeType === 'branch') return `${baseKey}__branch__${scopeId}`;
  return baseKey;
}

/**
 * حل قيمة إعداد واحدة من سلسلة سجلات، مع دمج الكائنات ودعم تخصيص البراند والفرع.
 */
export function resolveSetting<T>(
  records: readonly SettingRecord[],
  chain: readonly SettingScopeChainEntry[],
  key: string,
  fallback: T,
): T {
  const ordered = [...chain].sort((a, b) => SCOPE_PRIORITY[a.scopeType] - SCOPE_PRIORITY[b.scopeType]);
  const companyEntry = ordered.find((e) => e.scopeType === 'company');
  let result: unknown = fallback;
  for (const entry of ordered) {
    let record = records.find(
      (r) => r.key === key && r.scopeType === entry.scopeType && r.scopeId === entry.scopeId,
    );
    if (!record && companyEntry && (entry.scopeType === 'brand' || entry.scopeType === 'branch')) {
      const scopedKey = buildScopedSettingKey(key, entry.scopeType, entry.scopeId);
      record = records.find(
        (r) => r.scopeType === 'company' && r.scopeId === companyEntry.scopeId && r.key === scopedKey,
      );
    }
    if (!record) continue;
    result = mergeValue(result, record.value);
  }
  return result as T;
}

function mergeValue(base: unknown, override: unknown): unknown {
  if (
    base !== null &&
    override !== null &&
    typeof base === 'object' &&
    typeof override === 'object' &&
    !Array.isArray(base) &&
    !Array.isArray(override)
  ) {
    return { ...(base as Record<string, unknown>), ...(override as Record<string, unknown>) };
  }
  return override;
}

export function resolveDefaultRecurrence(
  records: readonly SettingRecord[],
  chain: readonly SettingScopeChainEntry[],
): RecurrenceRule {
  const value = resolveSetting<Partial<RecurrenceRule>>(
    records,
    chain,
    SETTING_KEYS.defaultRecurrence,
    DEFAULT_RECURRENCE,
  );
  return { ...DEFAULT_RECURRENCE, ...value };
}

export interface ResolvedSettings {
  timeZone: string;
  locale: string;
  defaultRecurrence: RecurrenceRule;
  autoGenerateRecurring: boolean;
  completedTaskRetention: CompletedTaskRetentionPolicy;
  operationalFeatures: OperationalFeaturesConfig;
  mediaHubPolicy: MediaHubRetentionPolicy;
  templateChecklists: Record<string, TemplateChecklistStepDef[]>;
  approvalPolicy: ApprovalPolicy;
  personalTaskPolicy: PersonalTaskPolicy;
  notificationPolicy: NotificationPolicy;
  retentionPolicy: RetentionPolicy;
  uploadPolicy: UploadPolicy;
  privacyPolicy: PrivacyPolicy;
  theme: ThemeTokens;
}

export function resolveAllSettings(
  records: readonly SettingRecord[],
  chain: readonly SettingScopeChainEntry[],
  defaults: { timeZone: string; locale?: string } = { timeZone: 'Asia/Riyadh' },
): ResolvedSettings {
  const orgChain = chain.filter((entry) => entry.scopeType !== 'user');
  return {
    timeZone: resolveSetting(records, chain, SETTING_KEYS.timeZone, defaults.timeZone),
    locale: resolveSetting(records, chain, SETTING_KEYS.locale, defaults.locale ?? 'ar'),
    defaultRecurrence: resolveDefaultRecurrence(records, chain),
    autoGenerateRecurring: resolveSetting(
      records,
      orgChain,
      SETTING_KEYS.autoGenerateRecurring,
      false,
    ),
    completedTaskRetention: {
      ...DEFAULT_COMPLETED_TASK_RETENTION,
      ...resolveSetting<Partial<CompletedTaskRetentionPolicy>>(
        records,
        orgChain,
        SETTING_KEYS.completedTaskRetention,
        DEFAULT_COMPLETED_TASK_RETENTION,
      ),
    },
    operationalFeatures: {
      ...DEFAULT_OPERATIONAL_FEATURES,
      ...resolveSetting<Partial<OperationalFeaturesConfig>>(
        records,
        orgChain,
        SETTING_KEYS.operationalFeatures,
        DEFAULT_OPERATIONAL_FEATURES,
      ),
    },
    mediaHubPolicy: {
      ...DEFAULT_MEDIA_HUB_POLICY,
      ...resolveSetting<Partial<MediaHubRetentionPolicy>>(
        records,
        orgChain,
        SETTING_KEYS.mediaHubPolicy,
        DEFAULT_MEDIA_HUB_POLICY,
      ),
    },
    templateChecklists: resolveSetting<Record<string, TemplateChecklistStepDef[]>>(
      records,
      orgChain,
      SETTING_KEYS.templateChecklists,
      {},
    ),
    approvalPolicy: resolveSetting(records, chain, SETTING_KEYS.approvalPolicy, DEFAULT_APPROVAL_POLICY),
    personalTaskPolicy: resolveSetting(
      records,
      chain,
      SETTING_KEYS.personalTaskPolicy,
      DEFAULT_PERSONAL_TASK_POLICY,
    ),
    notificationPolicy: resolveSetting(
      records,
      chain,
      SETTING_KEYS.notificationPolicy,
      DEFAULT_NOTIFICATION_POLICY,
    ),
    retentionPolicy: resolveSetting(records, chain, SETTING_KEYS.retentionPolicy, DEFAULT_RETENTION_POLICY),
    uploadPolicy: resolveSetting(records, chain, SETTING_KEYS.uploadPolicy, DEFAULT_UPLOAD_POLICY),
    privacyPolicy: resolveSetting(records, chain, SETTING_KEYS.privacyPolicy, DEFAULT_PRIVACY_POLICY),
    theme: resolveSetting(records, chain, SETTING_KEYS.theme, DEFAULT_THEME),
  };
}

/** حساب تاريخ انتهاء الاحتفاظ لمرفق جديد وفق السياسة. */
export function retentionExpiry(policy: RetentionPolicy, uploadedAt: Date): string | null {
  if (policy.mode === 'indefinite' || !policy.days) return null;
  return new Date(uploadedAt.getTime() + policy.days * 86_400_000).toISOString();
}

export function isRetentionEligible(
  policy: RetentionPolicy,
  attachment: { kind: string; createdAt: string },
  taskCompleted: boolean,
  now: Date = new Date(),
): boolean {
  if (policy.mode === 'indefinite' || !policy.days) return false;
  if (policy.imagesOnly && attachment.kind !== 'image') return false;
  if (policy.completedTasksOnly && !taskCompleted) return false;
  const ageMs = now.getTime() - new Date(attachment.createdAt).getTime();
  return ageMs >= policy.days * 86_400_000;
}
